import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router, ResolveStart, ResolveEnd } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { errorFormAnimation } from '../../../shared/animations/form-error';
import { Store } from '@ngrx/store';
import { AppState } from '../../../app.state';
import { takeUntil, skip, tap, filter } from 'rxjs/operators';
import {
  RequestAccessCaptchaAction,
  RequestAccessAction,
  RequestAccessErrorAction
} from 'src/app/core/actions/request-access/request-access.actions';
import {
  getRequestAccessCaptcha,
  getRequestAccessError,
  getRequestAccessLoading,
  getRequestAccessSuccess
} from '../../../core/selectors/request-access.selectors';
import { Subject, Observable } from 'rxjs';
import { NotificationService } from 'src/app/core/services/notification/notification.service';
import { getIsAnimationVisible } from 'src/app/core/selectors/animations.selector';
import { ShowLoadAnimation, HideLoadAnimation } from 'src/app/core/actions/animations/animations.actions';
import { TranslateService } from '@ngx-translate/core';
import { InvisibleReCaptchaComponent } from 'ngx-captcha';
import { EnvironmentResolveService } from 'src/app/core/services/environment-resolve/environment-resolve.service';

@Component({
  selector: 'app-request-access-page',
  templateUrl: './request-access-page.component.html',
  styleUrls: ['./request-access-page.component.scss'],
  animations: [errorFormAnimation()]
})
export class RequestAccessPageComponent implements OnInit, OnDestroy {
  public appVersion = environment.appVersion;
  public requestAccessForm: FormGroup;
  public requestAccessError = false;
  public isServerError = false;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public requestAccessLoading$: Observable<boolean>;
  public requestAccessSuccess$: Observable<boolean>;
  public showAnimation$: Observable<boolean>;
  private captchaResponse: string;

  public readonly siteKey = this.erService.environmentERS.reCaptchaSiteKey;
  public recaptcha: any = null;
  @ViewChild('captchaElem')
  captchaElem: InvisibleReCaptchaComponent;

  public get requestorName(): FormControl {
    return this.requestAccessForm.get('requestorName') as FormControl;
  }

  public get requestorEmail(): FormControl {
    return this.requestAccessForm.get('requestorEmail') as FormControl;
  }

  public get requestorCompany(): FormControl {
    return this.requestAccessForm.get('requestorCompany') as FormControl;
  }

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private store: Store<AppState>,
    private translateService: TranslateService,
    private erService: EnvironmentResolveService
  ) {}

  ngOnInit(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event instanceof ResolveStart) {
        this.store.dispatch(ShowLoadAnimation());
      }
      if (event instanceof ResolveEnd) {
        this.store.dispatch(HideLoadAnimation());
      }
    });

    this.showAnimation$ = this.store.select(getIsAnimationVisible);

    this.requestAccessForm = this.fb.group({
      requestorName: ['', Validators.required],
      requestorEmail: ['', [Validators.required, Validators.email]],
      requestorCompany: ['', Validators.required]
    });

    this.requestAccessLoading$ =
      this.store.select(getRequestAccessCaptcha) || this.store.select(getRequestAccessLoading);

    this.requestAccessSuccess$ = this.store.select(getRequestAccessSuccess);

    this.store
      .select(getRequestAccessError)
      .pipe(
        takeUntil(this.destroy$),
        filter(error => Boolean(error))
      )
      .subscribe(({ message }) => {
        this.isServerError = true;
        this.captchaElem.resetCaptcha();
        this.notificationService.showErrorNotification(message);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  handleCaptcha(): void {
    this.store.dispatch(RequestAccessCaptchaAction({}));
    this.captchaElem.execute();
  }

  handleSuccess(captchaResponse: string) {
    this.captchaResponse = `${captchaResponse}`;
    if (!this.captchaResponse) {
      this.translateService.get('CAPTCHA_ARE_YOU_ROBOT').subscribe((text: string) => {
        this.store.dispatch(RequestAccessErrorAction({ error: new Error(text) }));
      });

      return;
    }

    this.requestAccess();
  }

  handleExpire(): void {
    this.translateService.get('CAPTCHA_EXPIRE').subscribe((text: string) => {
      this.notificationService.showErrorNotification(text);
    });
  }

  handleError(): void {
    this.translateService.get('CAPTCHA_ERROR').subscribe((text: string) => {
      this.store.dispatch(RequestAccessErrorAction({ error: new Error(text) }));
    });
  }

  public requestAccess(): void {
    this.store.dispatch(
      RequestAccessAction({
        requestor: {
          name: this.requestorName.value,
          email: this.requestorEmail.value,
          company: this.requestorCompany.value,
          token: this.captchaResponse
        }
      })
    );
  }
}
